import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../Navbar';
import { Container, Row, Col, Card, Button, Spinner, Table } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { API_ENDPOINTS } from '../config/api';
import PageTitle from '../components/PageTitle';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';

const COLORS = ['#003F5C', '#58508D', '#BC5090', '#FF6361', '#FFA600', '#2D87BB', '#64C2A6', '#e4bf58', '#007ED6', '#6050DC'];

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error);
    console.error('Error Info:', errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-vh-100">
          <Navbar pageTitle="Survey Results" />
          <Container className="py-4">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Something went wrong</h4>
              <p>We encountered an error while loading the survey results.</p>
              <hr />
              <p className="mb-0">
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Try Again
                </Button>
                <Button 
                  variant="secondary" 
                  className="ms-2" 
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </p>
            </div>
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}

// Chart Components
const BarChartComponent = ({ data, questionType }) => {
  try {
    console.log('BarChart data:', data);
    
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-bar-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No responses yet for this {questionType} question</p>
        </div>
      );
    }

    // Process the responses to count occurrences
    const responseCounts = {};
    data.forEach(response => {
      if (!response || !response.answer) return;
      
      if (Array.isArray(response.answer)) {
        // For checkbox questions
        response.answer.forEach(ans => {
          if (ans) {
            responseCounts[ans] = (responseCounts[ans] || 0) + 1;
          }
        });
      } else {
        // For single answer questions
        responseCounts[response.answer] = (responseCounts[response.answer] || 0) + 1;
      }
    });

    // Convert to array format for charts
    const chartData = Object.entries(responseCounts).map(([option, count]) => ({
      option: option,
      count: count
    }));

    console.log('Processed chart data:', chartData);

    if (chartData.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-bar-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No valid responses for this {questionType} question</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
          <XAxis 
            dataKey="option" 
            angle={-45} 
            textAnchor="end" 
            height={80}
            tick={{ fontSize: 12, fill: '#6c757d' }}
            axisLine={{ stroke: '#e9ecef' }}
          />
          <YAxis 
            label={{ value: '# of Responses', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
            tick={{ fontSize: 12, fill: '#6c757d' }}
            axisLine={{ stroke: '#e9ecef' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
          <Bar 
            dataKey="count" 
            fill="#58508D"
            radius={[4, 4, 0, 0]}
            barSize={30}
            name="Number of Responses"
          />
        </BarChart>
      </ResponsiveContainer>
    );
  } catch (error) {
    console.error('Error in BarChartComponent:', error);
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 48, opacity: 0.3 }}></i>
        <p className="mt-2 text-danger">Error displaying chart</p>
      </div>
    );
  }
};

const PieChartComponent = ({ data }) => {
  try {
    console.log('PieChart data:', data);
    
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-pie-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No responses yet for this rating question</p>
        </div>
      );
    }

    // Process the responses to count occurrences
    const responseCounts = {};
    data.forEach(response => {
      if (!response || !response.answer) return;
      responseCounts[response.answer] = (responseCounts[response.answer] || 0) + 1;
    });

    // Convert to array format for charts
    const chartData = Object.entries(responseCounts).map(([rating, count]) => ({
      rating: rating,
      count: count
    }));

    console.log('Processed pie chart data:', chartData);

    if (chartData.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-pie-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No valid responses for this rating question</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="count"
            nameKey="rating"
            cx="50%"
            cy="50%"
            outerRadius={80}
            innerRadius={40}
            label={({ rating, percent }) => `${rating} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  } catch (error) {
    console.error('Error in PieChartComponent:', error);
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 48, opacity: 0.3 }}></i>
        <p className="mt-2 text-danger">Error displaying chart</p>
      </div>
    );
  }
};

const formatTextAnswer = (response) => {
  const raw = response?.answer ?? response?.text ?? response?.response;
  if (raw === undefined || raw === null || raw === '') return null;
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
};

const TextResponses = ({ responses, questionType }) => {
  try {
    console.log('Text responses:', responses);
    
    if (!responses || responses.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-chat-text" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No responses yet for this {questionType} question</p>
        </div>
      );
    }

    return (
      <div className="mt-3">
        <h6 className="text-muted mb-3 fw-semibold">Recent Responses:</h6>
        {responses.slice(0, 5).map((response, idx) => {
          const display = formatTextAnswer(response);
          return (
          <div key={idx} className="mb-2 p-3 card-bg-container rounded border">
            <i className="bi bi-quote me-2 text-muted"></i>
            {display ?? '—'}
            {response.residentName && (
              <div className="small text-muted mt-1">{response.residentName}</div>
            )}
          </div>
        );
        })}
      </div>
    );
  } catch (error) {
    console.error('Error in TextResponses:', error);
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 48, opacity: 0.3 }}></i>
        <p className="mt-2 text-danger">Error displaying responses</p>
      </div>
    );
  }
};

// Add new RadioMatrixChart component
const RadioMatrixChart = ({ data, questionType }) => {
  try {
    console.log('RadioMatrix data:', data);
    
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-table" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No responses yet for this {questionType} question</p>
        </div>
      );
    }

    // Process the responses to count occurrences for each option
    const responseCounts = {};
    data.forEach(response => {
      if (!response || !response.answer) return;
      
      // For radio matrix, answer will be an object with option-value pairs
      Object.entries(response.answer).forEach(([option, value]) => {
        if (!responseCounts[option]) {
          responseCounts[option] = {};
        }
        responseCounts[option][value] = (responseCounts[option][value] || 0) + 1;
      });
    });

    // Convert to array format for charts
    const chartData = Object.entries(responseCounts).map(([option, counts]) => ({
      option: option,
      ...counts
    }));

    console.log('Processed radio matrix chart data:', chartData);

    if (chartData.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-table" style={{ fontSize: 48, opacity: 0.3 }}></i>
          <p className="mt-2">No valid responses for this {questionType} question</p>
        </div>
      );
    }

    // Get all possible values (ratings) from the data
    const allValues = [...new Set(data.flatMap(r => 
      r.answer ? Object.values(r.answer) : []
    ))].sort();

    return (
      <div className="mt-4">
        {chartData.map((item, index) => (
          <div key={index} className="mb-4">
            <h6 className="text-muted mb-3 fw-semibold">{item.option}</h6>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[item]} margin={{ top: 20, right: 20, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis dataKey="option" />
                <YAxis />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                {allValues.map((value, i) => (
                  <Bar 
                    key={value} 
                    dataKey={value.toString()} 
                    fill={COLORS[i % COLORS.length]} 
                    name={`Rating ${value}`}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    );
  } catch (error) {
    console.error('Error in RadioMatrixChart:', error);
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 48, opacity: 0.3 }}></i>
        <p className="mt-2 text-danger">Error displaying chart</p>
      </div>
    );
  }
};

const SurveyResultsPage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInstitutionalAdmin =
    localStorage.getItem('role') === 'institutional_admin' ||
    location.pathname.startsWith('/institutional-admin/');
  const apiBase = isInstitutionalAdmin ? API_ENDPOINTS.INSTITUTIONAL_ADMIN : API_ENDPOINTS.DIRECTOR;

  useEffect(() => {
    const fetchSurveyResults = async () => {
      try {
        const token = localStorage.getItem("token");
        console.log('Fetching survey results for ID:', surveyId);
        
        const response = await fetch(`${apiBase}/survey-results/${surveyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('API Error:', errorData);
          throw new Error(errorData.message || 'Failed to fetch survey results');
        }

        const data = await response.json();
        console.log('Raw API Response:', data);
        
        if (!data || !data.questions) {
          console.error('Invalid data structure:', data);
          throw new Error('Invalid survey data structure');
        }

        // Process and validate the data
        const processedData = {
          ...data,
          totalResponses: parseInt(data.totalResponses) || 0,
          completedResponses: parseInt(data.completedResponses) || 0,
          totalAssigned: parseInt(data.totalAssigned) || 0,
          averageScore: parseFloat(data.averageScore) || 0,
          questions: data.questions.map(question => ({
            ...question,
            responses: Array.isArray(question.responses) ? question.responses : []
          }))
        };

        console.log('Processed survey data:', processedData);
        setSurveyData(processedData);
      } catch (err) {
        console.error('Error fetching survey results:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyResults();
  }, [surveyId, apiBase]);

  // Calculate metrics
  const calculateMetrics = (data) => {
    if (!data) return { completionRate: 0, averageScore: 0, responseRate: 0 };

    const completionRate = data.totalResponses && data.totalAssigned 
      ? (data.completedResponses / data.totalAssigned) * 100 
      : 0;

    const responseRate = data.totalResponses && data.totalAssigned 
      ? (data.totalResponses / data.totalAssigned) * 100 
      : 0;

    return {
      completionRate: completionRate.toFixed(1),
      averageScore: data.averageScore ? data.averageScore.toFixed(1) : '0.0',
      responseRate: responseRate.toFixed(1)
    };
  };

  const metrics = calculateMetrics(surveyData);
  const breadcrumbItems = [
    { href: '/institutional-admin/dashboard', label: 'Dashboard' },
    { href: '/institutional-admin/evaluations', label: 'Evaluations' },
    { label: 'Survey Results' },
  ];

  if (loading) {
    return (
      <div className="min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Survey Results" />
        <Container className="py-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading survey results...</span>
              </div>
              <p className="mt-3 text-muted">Loading survey results...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100">
        <PageTitle title="Survey Error" />
        <Navbar pageTitle="Survey Results" />
        <Container className="py-4">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </Container>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div className="min-vh-100">
        <PageTitle title="No Survey Data" />
        <Navbar pageTitle="Survey Results" />
        <Container className="py-4">
          <div className="alert alert-info" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            No survey data found.
          </div>
        </Container>
      </div>
    );
  }

  console.log('Rendering survey data:', surveyData);

  const renderQuestionCards = () => (
    <div className="row">
      {surveyData.questions && surveyData.questions.map((question, index) => {
        console.log(`Rendering question ${index}:`, question);
        const breakdownRows = getResponseBreakdownRows(question.responses);
        const totalCount = breakdownRows.reduce((sum, [, count]) => sum + count, 0);
        return (
          <div className="col-lg-6 col-md-12 mb-4" key={index}>
            <div className="border-0 rounded p-4 card-bg-container">
              <h6 className="text-muted mb-3 fw-semibold">
                <i className={`bi ${getQuestionIcon(question.questionType)} me-2 text-primary`}></i>
                {question.questionText || 'Untitled Question'}
              </h6>
              
              {question.questionType === 'multiple_choice' && (
                <BarChartComponent data={question.responses} questionType="multiple choice" />
              )}

              {question.questionType === 'rating' && (
                <PieChartComponent data={question.responses} />
              )}

              {(question.questionType === 'text' || question.questionType === 'textarea') && (
                <TextResponses responses={question.responses} questionType="text" />
              )}

              {question.questionType === 'radio-matrix' && (
                <RadioMatrixChart data={question.responses} questionType="radio matrix" />
              )}

              {(question.questionType === 'checkbox' || question.questionType === 'radio' || question.questionType === 'dropdown') && (
                <BarChartComponent data={question.responses} questionType="selection" />
              )}

              {question.questionType === 'header-paragraph' && (
                <div className="text-muted">
                  <small>This is a header/paragraph section</small>
                </div>
              )}

              {question.questionType !== 'text' &&
                question.questionType !== 'textarea' &&
                question.questionType !== 'header-paragraph' && (
                <div className="mt-4">
                  <h6 className="text-muted mb-2 fw-semibold">Response Breakdown</h6>
                  <Table responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Answer</th>
                        <th>Count</th>
                        <th>Percent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownRows.length ? breakdownRows.map(([label, count]) => (
                        <tr key={label}>
                          <td>{label}</td>
                          <td>{count}</td>
                          <td>{totalCount > 0 ? `${((count / totalCount) * 100).toFixed(1)}%` : '0.0%'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={3} className="text-muted">No responses yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isInstitutionalAdmin) {
    return (
      <ErrorBoundary>
        <div className="d-flex vh-100">
          <SuperAdminSidebar variant="institutional" />
          <div className="d-flex flex-column flex-grow-1">
            <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
            <div className="flex-grow-1" style={{ height: 'calc(100vh - 61px)' }}>
              <Container fluid className="h-100 px-0">
                <Row className="g-0 h-100">
                  <Col md={3} className="bg-inverse-light border-end p-3">
                    <div className="position-sticky" style={{ top: '1rem' }}>
                      <h5 className="mb-1">{surveyData.surveyTitle || 'Untitled Survey'}</h5>
                      <div className="text-muted small mb-3">
                        {surveyData.createdAt ? new Date(surveyData.createdAt).toLocaleDateString() : 'Date not available'}
                      </div>

                        <Row lg={1} md={1} sm={2} xs={2} className="g-2">
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="py-2">
                            <small className="text-muted d-block">Completion Rate</small>
                            <div className="fw-semibold">{metrics.completionRate}%</div>
                          </Card.Body>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="py-2">
                            <small className="text-muted d-block">Average Score</small>
                            <div className="fw-semibold">{metrics.averageScore}</div>
                          </Card.Body>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="py-2">
                            <small className="text-muted d-block">Response Rate</small>
                            <div className="fw-semibold">{metrics.responseRate}%</div>
                          </Card.Body>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="py-2">
                            <small className="text-muted d-block">Questions</small>
                            <div className="fw-semibold">{surveyData.questions?.length || 0}</div>
                          </Card.Body>
                        </Card>
                        </Row>
                     
                    </div>
                  </Col>
                  <Col md={9} className="bg-inverse-light p-3" style={{ overflowY: 'auto', height: '100%' }}>
                    {renderQuestionCards()}
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-vh-100">
        <PageTitle title="Survey Results" description="View survey results and response analytics" />
        <Navbar pageTitle="Survey Results" />
        
        <Container className="py-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">{surveyData.surveyTitle || 'Untitled Survey'}</h2>
              <p className="text-muted mb-0">
                {surveyData.totalResponses || 0} responses | 
                Created: {surveyData.createdAt ? new Date(surveyData.createdAt).toLocaleDateString() : 'Date not available'}
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() =>
                navigate(isInstitutionalAdmin ? '/institutional-admin/evaluations' : '/evaluations')
              }
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to Evaluations
            </Button>
          </div>

          {/* Summary Statistics Cards */}
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
              <div className="text-center p-3 border rounded h-100 card-bg-container">
                <i className="bi bi-check-circle-fill text-success mb-2" style={{ fontSize: 24 }} />
                <h4 className="mb-1">{metrics.completionRate}%</h4>
                <small className="text-muted">Completion Rate</small>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
              <div className="text-center p-3 border rounded h-100 card-bg-container">
                <i className="bi bi-star-fill text-warning mb-2" style={{ fontSize: 24 }} />
                <h4 className="mb-1">{metrics.averageScore}</h4>
                <small className="text-muted">Average Score</small>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
              <div className="text-center p-3 border rounded h-100 card-bg-container">
                <i className="bi bi-people-fill text-primary mb-2" style={{ fontSize: 24 }} />
                <h4 className="mb-1">{metrics.responseRate}%</h4>
                <small className="text-muted">Response Rate</small>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
              <div className="text-center p-3 border rounded h-100 card-bg-container">
                <i className="bi bi-file-text-fill text-info mb-2" style={{ fontSize: 24 }} />
                <h4 className="mb-1">{surveyData.questions?.length || 0}</h4>
                <small className="text-muted">Total Questions</small>
              </div>
            </div>
          </div>

          {/* Question Results */}
          {renderQuestionCards()}
        </Container>
      </div>
    </ErrorBoundary>
  );
};

// Helper function to get appropriate icon for question type
const getQuestionIcon = (questionType) => {
  switch (questionType) {
    case 'multiple_choice':
      return 'bi-list-check';
    case 'rating':
      return 'bi-star';
    case 'text':
    case 'textarea':
      return 'bi-chat-text';
    case 'radio-matrix':
      return 'bi-table';
    case 'checkbox':
      return 'bi-check-square';
    case 'radio':
      return 'bi-record-circle';
    case 'dropdown':
      return 'bi-list';
    case 'header-paragraph':
      return 'bi-text-paragraph';
    default:
      return 'bi-question-circle';
  }
};

const getResponseBreakdownRows = (responses = []) => {
  const counts = {};
  responses.forEach((response) => {
    const answer = response?.answer;
    if (answer === undefined || answer === null || answer === '') return;

    if (Array.isArray(answer)) {
      answer.forEach((item) => {
        const key = String(item);
        counts[key] = (counts[key] || 0) + 1;
      });
      return;
    }

    if (typeof answer === 'object') {
      Object.values(answer).forEach((item) => {
        const key = String(item);
        counts[key] = (counts[key] || 0) + 1;
      });
      return;
    }

    const key = String(answer);
    counts[key] = (counts[key] || 0) + 1;
  });

  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

export default SurveyResultsPage; 